import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Clock, Mail, AlertCircle } from "lucide-react"

export default function SettingsPage() {
  const [batchSchedule, setBatchSchedule] = useState({
    morning: "07:00",
    evening: "23:00",
  })

  const [emailSettings, setEmailSettings] = useState({
    smtpServer: "smtp.gmail.com",
    smtpPort: 587,
    senderEmail: "agenVX@example.com",
  })

  const [platforms, setPlatforms] = useState([
    { name: "Instagram", email: "abuse@instagram.com", enabled: true },
    { name: "Facebook", email: "abuse@facebook.com", enabled: true },
    { name: "Twitter", email: "abuse@twitter.com", enabled: true },
    { name: "TikTok", email: "abuse@tiktok.com", enabled: true },
    { name: "YouTube", email: "abuse@youtube.com", enabled: true },
  ])

  const handleTogglePlatform = (index: number) => {
    const updated = [...platforms]
    updated[index].enabled = !updated[index].enabled
    setPlatforms(updated)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-2">Konfigurasi sistem batch job dan notifikasi</p>
      </div>

      {/* Batch Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Batch Job Schedule
          </CardTitle>
          <CardDescription>Atur waktu crawling otomatis setiap hari</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Morning Batch</label>
              <input
                type="time"
                value={batchSchedule.morning}
                onChange={(e) => setBatchSchedule({ ...batchSchedule, morning: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-muted-foreground mt-1">Crawling dimulai setiap pagi</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Evening Batch</label>
              <input
                type="time"
                value={batchSchedule.evening}
                onChange={(e) => setBatchSchedule({ ...batchSchedule, evening: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-muted-foreground mt-1">Crawling dimulai setiap malam</p>
            </div>
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-700">Save Schedule</Button>
        </CardContent>
      </Card>

      {/* Email Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Configuration
          </CardTitle>
          <CardDescription>Konfigurasi SMTP untuk pengiriman laporan</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">SMTP Server</label>
            <input
              type="text"
              value={emailSettings.smtpServer}
              onChange={(e) => setEmailSettings({ ...emailSettings, smtpServer: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">SMTP Port</label>
              <input
                type="number"
                value={emailSettings.smtpPort}
                onChange={(e) =>
                  setEmailSettings({
                    ...emailSettings,
                    smtpPort: Number.parseInt(e.target.value),
                  })
                }
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Sender Email</label>
              <input
                type="email"
                value={emailSettings.senderEmail}
                onChange={(e) => setEmailSettings({ ...emailSettings, senderEmail: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-700">Test Connection</Button>
        </CardContent>
      </Card>

      {/* Platform Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Platform Report Recipients
          </CardTitle>
          <CardDescription>Kelola email penerima laporan untuk setiap platform</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {platforms.map((platform, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <p className="font-semibold text-foreground">{platform.name}</p>
                  <p className="text-sm text-muted-foreground">{platform.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={platform.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                    {platform.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                  <button
                    onClick={() => handleTogglePlatform(idx)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      platform.enabled ? "bg-green-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        platform.enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700">Save Configuration</Button>
        </CardContent>
      </Card>
    </div>
  )
}

